import React from "react";
import { type FormikErrors, type FormikTouched } from "formik";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { Prize, type Config } from "kadow-jetpack-model";
import { tt } from "../../../../utils/i18n/i18n";
import PrizesConfiguratorDisplayCard from "../../../19_JetpackConfig/components/GiftsConfiguration/component/PrizesConfiguratorDisplayCard";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import Carousel from "../../../../components/Carousel/Carousel";
import ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import { CarouselAddPrizeButton } from "./CarouselAddPrizeButton";
import { enrichGiftWithAdvancedCouponFeatures } from "../../../../utils/helpers/buildBaseGift";
import PrizeTemplateDTO from "../../../../model/PrizeTemplateDTO";

export interface OnboardingGiftsCarouselProps {
  values: Config;
  availableTemplates: PrizeTemplateDTO[];
  allPrizeTemplates: PrizeTemplateDTO[];
  setFieldValue: (field: string, value: any) => void;
  accountToDisplay: ManagedAccount | undefined | null;
  setEditingIndex: (index: number | null) => void;
  onAddPrize: () => void;
  errors: FormikErrors<Config>;
  touched: FormikTouched<Config>;
}

export default function OnboardingGiftsCarousel(
  props: OnboardingGiftsCarouselProps
) {
  const isTemplateSelected = (templateId: string): boolean => {
    return props.values.prizes.some((prize) => prize.id === templateId);
  };

  const onPrizeTemplateClicked = (
    isSelected: boolean,
    templateId: string,
    template: Prize
  ) => {
    if (isSelected) {
      const updatedPrizes = props.values.prizes.filter(
        (prize) => prize.tid !== templateId
      );
      props.setFieldValue("prizes", updatedPrizes);
    } else {
      const enrichedPrize = enrichGiftWithAdvancedCouponFeatures(
        props.accountToDisplay,
        template
      );
      const prizes = JSON.parse(JSON.stringify(props.values?.prizes ?? []));
      //@ts-ignore
      delete enrichedPrize.isTemplate;
      enrichedPrize.tid = templateId;
      prizes.push(enrichedPrize);
      props.setFieldValue("prizes", prizes);
    }
  };

  const templateSlides = props.allPrizeTemplates.map((template, index) => {
    const isSelected = isTemplateSelected(template.id);
    return (
      <div key={`template-${template.origId || index}`}>
        <KC_Button
          id={template.origId}
          isTransparent
          isPrimary={isSelected}
          className={`bordered-btn ${isSelected && "selected-btn"} select-gift-button`}
          size={KC_ButtonSize.Large}
          text={isSelected ? tt("selectedText") : tt("game_select")}
          showIconOnTheRight={isSelected}
          icon={isSelected ? faCheckCircle : undefined}
          onClick={() =>
            onPrizeTemplateClicked(isSelected, template.id, template)
          }
        />
        <PrizesConfiguratorDisplayCard
          isPrize
          isEditable={false}
          title={template.name}
          secondaryText={`${template.pc?.toString() || "0"}%${template.iQty ? tt("prizes_configurator_max_per_week", { quantity: template.iQty }) : ""}`}
          img={template.image}
          onEdit={() => {}}
        />
      </div>
    );
  });

  const configPrizeSlides = props.values.prizes
    .map((prize, configIndex) => {
      const originalIndex = props.values.prizes.findIndex(
        (p) => p.id === prize.id
      );
      const template = props.allPrizeTemplates.find((t) => t.id === prize.tid);
      if (template) {
        return null;
      }
      return (
        <div key={`prize-${prize.id || configIndex}`}>
          <KC_Button
            id={prize.id}
            isTransparent
            isPrimary={true}
            className={`bordered-btn selected-btn select-gift-button`}
            size={KC_ButtonSize.Large}
            text={tt("selectedText")}
            showIconOnTheRight={true}
            icon={faCheckCircle}
          />
          <PrizesConfiguratorDisplayCard
            isPrize
            isEditable
            title={prize.name}
            secondaryText={`${prize.pc?.toString() || "0"}%${prize.iQty ? tt("prizes_configurator_max_per_week", { quantity: prize.iQty }) : ""}`}
            img={prize.image}
            onEdit={() => props.setEditingIndex(originalIndex)}
          />
        </div>
      );
    })
    .filter(Boolean);

  return (
    <Carousel
      baseId="gift-selector"
      style={{ marginLeft: 16 }}
      slides={[
        ...templateSlides,
        ...configPrizeSlides,
        <CarouselAddPrizeButton key="add-button" onClick={props.onAddPrize} />
      ]}
    />
  );
}
